const AIRPORT_SEARCH_INDEX_PATH = `${
  process.env.PUBLIC_URL || ''
}/earth-assets/data/airport-search-index.json`;

const removeDiacritics = (value) =>
  value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

export const normalizeAirportQuery = (value) =>
  removeDiacritics(String(value || ''))
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');

export const getAirportDisplayCode = (airport) =>
  airport?.displayCode || airport?.iata || airport?.code || airport?.icao || 'AIRPORT';

export const getAirportLookupCode = (airport) =>
  airport?.lookupCode || airport?.iata || airport?.code || airport?.icao || '';

export const getAirportLocationLabel = (airport) =>
  [airport?.city, airport?.country].filter(Boolean).join(' / ');

const scoreAirportMatch = (airport, normalizedQuery) => {
  if (!normalizedQuery) return Number.NEGATIVE_INFINITY;

  const queryWords = normalizedQuery.split(' ').filter(Boolean);
  const queryCompact = normalizedQuery.replace(/\s+/g, '');
  const iata = (airport.iata || '').toLowerCase();
  const icao = (airport.icao || '').toLowerCase();
  const name = normalizeAirportQuery(airport.name || '');
  const city = normalizeAirportQuery(airport.city || '');
  const country = normalizeAirportQuery(airport.country || '');
  const searchText = airport.searchText || [name, city, country, iata, icao].join(' ').trim();
  const searchCompact = searchText.replace(/\s+/g, '');

  let score = Number.NEGATIVE_INFINITY;

  if (iata && iata === normalizedQuery) score = Math.max(score, 1400);
  if (icao && icao === normalizedQuery) score = Math.max(score, 1360);
  if (name && name === normalizedQuery) score = Math.max(score, 1280);
  if (city && city === normalizedQuery) score = Math.max(score, 1160);
  if (country && country === normalizedQuery) score = Math.max(score, 1080);

  if (iata && iata.startsWith(normalizedQuery)) score = Math.max(score, 1220 - iata.length);
  if (icao && icao.startsWith(normalizedQuery)) score = Math.max(score, 1180 - icao.length);
  if (name && name.startsWith(normalizedQuery)) score = Math.max(score, 1120 - name.length * 0.1);
  if (city && city.startsWith(normalizedQuery)) score = Math.max(score, 1020 - city.length * 0.1);
  if (country && country.startsWith(normalizedQuery)) {
    score = Math.max(score, 980 - country.length * 0.1);
  }

  const nameWordMatch = name.split(' ').some((word) => word.startsWith(normalizedQuery));
  const cityWordMatch = city.split(' ').some((word) => word.startsWith(normalizedQuery));
  if (nameWordMatch) score = Math.max(score, 940);
  if (cityWordMatch) score = Math.max(score, 900);

  const includeIndex = searchText.indexOf(normalizedQuery);
  if (includeIndex >= 0) score = Math.max(score, 780 - includeIndex * 0.4);

  const compactIndex = queryCompact ? searchCompact.indexOf(queryCompact) : -1;
  if (compactIndex >= 0) score = Math.max(score, 720 - compactIndex * 0.3);

  if (queryWords.length > 1) {
    const queryWordSet = new Set(queryWords);
    const searchWordSet = new Set(searchText.split(' ').filter(Boolean));
    const matchedWordCount = Array.from(queryWordSet).filter((word) => searchWordSet.has(word)).length;

    if (matchedWordCount === queryWordSet.size) {
      score = Math.max(score, 860 + matchedWordCount * 12);
    } else if (matchedWordCount > 1) {
      score = Math.max(score, 500 + matchedWordCount * 8);
    }
  }

  if (!Number.isFinite(score)) {
    return Number.NEGATIVE_INFINITY;
  }

  if (airport.iata) score += 16;
  if (airport.icao) score += 8;

  return score;
};

export const searchAirportIndex = (query, airportIndex, limit = 8) => {
  const normalizedQuery = normalizeAirportQuery(query);
  if (!normalizedQuery || !Array.isArray(airportIndex) || !airportIndex.length) {
    return [];
  }

  return airportIndex
    .map((airport) => ({
      airport,
      score: scoreAirportMatch(airport, normalizedQuery),
    }))
    .filter((entry) => Number.isFinite(entry.score))
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      const leftCode = getAirportDisplayCode(left.airport);
      const rightCode = getAirportDisplayCode(right.airport);
      if (leftCode !== rightCode) {
        return leftCode.localeCompare(rightCode);
      }

      return String(left.airport.name || '').localeCompare(String(right.airport.name || ''));
    })
    .slice(0, limit)
    .map((entry) => entry.airport);
};

export default AIRPORT_SEARCH_INDEX_PATH;
